/**
 * Contact-field answer rules for TEXT items whose `config.inputType` is
 * `email` or `tel`. These mirror the operations server's checks on public and
 * staff submissions so a customer sees the problem inline instead of a failed
 * submit. Empty answers are not judged here — `required` is handled separately.
 */

export const INVALID_EMAIL_MESSAGE = 'Enter a valid email address';
export const INVALID_PHONE_MESSAGE = 'Enter a 10-digit US or Canadian phone number';

/** One `@`, a non-empty local part, a dotted domain with no empty labels, no whitespace. */
export function isPlausibleEmail(value: string): boolean {
  const trimmed = value.trim();
  if (/\s/.test(trimmed)) return false;
  const parts = trimmed.split('@');
  if (parts.length !== 2) return false;
  const [local, domain] = parts as [string, string];
  if (!local || !domain) return false;
  const labels = domain.split('.');
  return labels.length >= 2 && labels.every((label) => label.length > 0);
}

/**
 * Exactly ten US/Canadian digits once spaces and `( ) - .` are removed, with an
 * optional leading `+1` / `1`. Length only, like the contacts module's
 * `isValidContactPhone`, so 555 test numbers stay valid.
 */
export function isNanpPhone(value: string): boolean {
  const compact = value.trim().replace(/[\s().-]/g, '');
  // At most one country prefix: "+1" then exactly ten digits, or bare digits
  // with an optional leading "1". "+1" plus "1" plus ten is eleven, not ten.
  if (compact.startsWith('+')) return /^\+1\d{10}$/.test(compact);
  return /^1?\d{10}$/.test(compact);
}

export function contactInputIssue(inputType: string | undefined, value: string): string | null {
  if (!value.trim()) return null;
  if (inputType === 'email') return isPlausibleEmail(value) ? null : INVALID_EMAIL_MESSAGE;
  if (inputType === 'tel') return isNanpPhone(value) ? null : INVALID_PHONE_MESSAGE;
  return null;
}
